import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

type PlanId = "starter" | "pro" | "unlimited";

// Public: client-side needs PayPal client id + plan ids to render the SDK button.
export const getPayPalConfig = createServerFn({ method: "GET" }).handler(async () => {
  const clientId = process.env.PAYPAL_CLIENT_ID;
  if (!clientId) throw new Error("PayPal is not configured");
  return {
    clientId,
    env: (process.env.PAYPAL_ENV || "live").toLowerCase(),
    planIds: {
      starter: process.env.PAYPAL_PLAN_ID_STARTER || process.env.PAYPAL_PLAN_STARTER || null,
      pro: process.env.PAYPAL_PLAN_ID_PRO || process.env.PAYPAL_PLAN_PRO || null,
      unlimited:
        process.env.PAYPAL_PLAN_ID_UNLIMITED || process.env.PAYPAL_PLAN_UNLIMITED || null,
    },
  };
});

// Called after the user approves the PayPal subscription in the popup.
// Persists the subscription id on the profile and activates the tier if the
// PayPal API reports the subscription as ACTIVE / APPROVED. The definitive
// state transitions still come from webhooks.
export const activatePayPalSubscription = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { plan: PlanId; subscriptionId: string }) => {
    if (!input?.plan || !["starter", "pro", "unlimited"].includes(input.plan)) {
      throw new Error("Invalid plan");
    }
    if (!input.subscriptionId || typeof input.subscriptionId !== "string") {
      throw new Error("Missing subscription id");
    }
    return input;
  })
  .handler(async ({ data, context }) => {
    const { userId, supabase } = context;

    const { data: profile } = await supabase
      .from("profiles")
      .select("role")
      .eq("id", userId)
      .maybeSingle();
    if ((profile as { role?: string } | null)?.role !== "freelancer") {
      throw new Error("Subscriptions are only available for freelancer accounts.");
    }

    const { paypalGet, paypalPlanIdFor } = await import("./paypal.server");
    const sub = await paypalGet<{
      id: string;
      status: string;
      plan_id: string;
      custom_id?: string;
      billing_info?: { next_billing_time?: string };
    }>(`/v1/billing/subscriptions/${encodeURIComponent(data.subscriptionId)}`);

    // Guard against tampering: subscription must be for the plan the user chose.
    const expectedPlanId = paypalPlanIdFor(data.plan);
    if (sub.plan_id !== expectedPlanId) {
      throw new Error("Subscription plan mismatch");
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Ownership proof: the subscription must have been created by this user
    // (custom_id is stamped with the authenticated user id at creation time).
    if (!sub.custom_id || sub.custom_id !== userId) {
      throw new Error("This subscription does not belong to your account.");
    }

    // A subscription id may never be re-pointed at a different profile.
    const { data: existing } = await supabaseAdmin
      .from("profiles")
      .select("id")
      .eq("paypal_subscription_id", sub.id)
      .maybeSingle();
    if (existing && (existing as { id?: string }).id !== userId) {
      throw new Error("This subscription is already linked to another account.");
    }


    const isActive = sub.status === "ACTIVE" || sub.status === "APPROVED";
    const renewsAt = sub.billing_info?.next_billing_time
      ? new Date(sub.billing_info.next_billing_time).toISOString()
      : null;

    const update: Record<string, unknown> = {
      paypal_subscription_id: sub.id,
      paypal_plan_id: sub.plan_id,
      subscription_status: sub.status.toLowerCase(),
      subscription_current_period_end: renewsAt,
      renewal_reminder_sent_for: null,
    };
    if (isActive) update.subscription_tier = data.plan;
    await supabaseAdmin.from("profiles").update(update as never).eq("id", userId);

    return { ok: true, status: sub.status };
  });
